import {
  MatchOptionList,
  MatchPairList,
} from 'APIs/letrusAPI/matchQuestion/types';
import {useEffect, useState} from 'react';

export interface Option {
  id: number;
  text: string;
  pairId?: number;
}

function useMatchQuestion(
  matchOptions?: MatchOptionList,
  matchResponses?: MatchPairList['responses'],
) {
  const [leftList, setLeftList] = useState<Option[]>([]);
  const [rightList, setRightList] = useState<Option[]>([]);
  const [activePair, setActivePair] = useState<number>();

  useEffect(() => {
    if (matchOptions?.pairs) {
      const newList = matchOptions.pairs.map((pair) => {
        // the API grades each answer by this id, so it can't be the item's position
        const leftOption = {
          id: pair.id,
          text: pair.left_item,
        };
        const rightOption = {...pair.right_option, pairId: undefined};
        return {leftOption, rightOption};
      });

      setLeftList(newList.map((list) => list.leftOption));
      setRightList(newList.map((list) => list.rightOption));
    }
  }, [matchOptions]);

  function updateSelection(optionId: number, listName?: string) {
    if (listName === 'left') {
      setActivePair(optionId);
      return;
    }

    if (listName === 'right') {
      setRightList(setPairIdOnRightItem(optionId));
      setActivePair(undefined);
      return;
    }
  }

  function setPairIdOnRightItem(optionId: number) {
    return rightList.map((option) => {
      if (optionId === option.id) {
        return {...option, pairId: activePair};
      }
      return {
        ...option,
        pairId: option.pairId === activePair ? undefined : option.pairId,
      };
    });
  }

  // 1-based position of the pair's left item, shown as the pair marker
  function pairNumber(pairId?: number) {
    const index = leftList.findIndex((option) => option.id === pairId);
    return index === -1 ? undefined : index + 1;
  }

  const completed = rightList.every((option) => !!option.pairId);

  const hasBeenAnswered = !!matchResponses?.length;

  const responses = matchResponses?.map((response) => ({
    pairId: response.pair_id,
    leftId: response.pair_id,
    rightId: Number(response.selected_option_id),
    isCorrect: response.status === 'correct',
  }));

  return {
    responses,
    completed,
    activePair,
    hasBeenAnswered,
    pairNumber,
    update: updateSelection,
    options: {
      left: leftList,
      right: rightList,
    },
  };
}

export default useMatchQuestion;
